import React from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Typography,
  Grid,
  Stack,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import {
  HelpCircle,
  Video,
  FileQuestion,
  ShieldCheck,
  Mail,
} from "lucide-react";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import { CommonInput, CommonButton } from "../../../../Components/ui";

const HelpHero = ({
  searchQuery = "",
  onSearchChange,
  videoCount = 13,
  faqCount = 14,
  onScrollToSection,
}) => {
  return (
    <Card
      sx={{
        p: { xs: 3, sm: 4 },
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.dashboard.cardBg,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardFlat,
        position: "relative",
        overflow: "hidden",
        mb: 4,
      }}
    >
      {/* Top Gradient Accent Stripe */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: colors.gradients.primary,
        }}
      />

      <Grid container spacing={3.5} alignItems="center">
        {/* Left: Title, Description & Search Bar */}
        <Grid item xs={12} lg={7}>
          <Stack spacing={2.5}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: borderRadiusPx.lg,
                  bgcolor: colors.blue[50],
                  color: colors.primary.main,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 8px rgba(37, 99, 235, 0.15)",
                }}
              >
                <HelpCircle size={24} />
              </Box>
              <Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 900,
                    color: colors.slate[900],
                    letterSpacing: "-0.5px",
                    fontSize: { xs: "1.75rem", sm: "2.25rem", md: "2.5rem" },
                    lineHeight: 1.15,
                  }}
                >
                  Help Center & Tutorials
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.primary.main,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Knowledge Base, Video Guides & Support
                </Typography>
              </Box>
            </Box>

            <Typography
              variant="body1"
              sx={{
                color: colors.slate[600],
                fontSize: { xs: "0.925rem", sm: "1rem" },
                maxWidth: 620,
                lineHeight: 1.6,
              }}
            >
              Explore our comprehensive step-by-step video tutorials and answers to frequently asked
              questions regarding playing games, creating decentralized lotteries, and managing rewards.
            </Typography>

            {/* Interactive Search Input */}
            <Box sx={{ maxWidth: 540, width: "100%" }}>
              <CommonInput
                fullWidth
                size="small"
                placeholder="Search tutorials, topics, rules, or questions..."
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: colors.slate[400], fontSize: 22 }} />
                  </InputAdornment>
                }
                endAdornment={
                  searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => onSearchChange("")} sx={{ p: 0.25 }}>
                        <ClearIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }
                sx={{
                  bgcolor: colors.background.paper,
                  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.04)",
                }}
              />
            </Box>

            {/* Quick Navigation Anchor Buttons */}
            <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ gap: 1 }}>
              <CommonButton
                variant="outline"
                size="small"
                onClick={() => onScrollToSection("videos")}
                startIcon={<Video size={16} />}
              >
                Video Tutorials ({videoCount})
              </CommonButton>
              <CommonButton
                variant="outline"
                size="small"
                onClick={() => onScrollToSection("faqs")}
                startIcon={<FileQuestion size={16} />}
              >
                Knowledgebase FAQs ({faqCount})
              </CommonButton>
              <CommonButton
                variant="outline"
                size="small"
                onClick={() => onScrollToSection("contact")}
                startIcon={<Mail size={16} />}
              >
                Get Support
              </CommonButton>
            </Stack>
          </Stack>
        </Grid>

        {/* Right: Feature Highlights Grid */}
        <Grid item xs={12} lg={5}>
          <Grid container spacing={2}>
            {/* Metric 1: Video Guides */}
            <Grid item xs={6}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: borderRadiusPx.lg,
                  bgcolor: colors.blue[50],
                  border: `1px solid ${colors.blue[200]}`,
                  height: "100%",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: borderRadiusPx.md,
                      bgcolor: colors.primary.main,
                      color: colors.neutral.white,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Video size={15} />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.primary.dark,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.7rem",
                    }}
                  >
                    Video Guides
                  </Typography>
                </Stack>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 900,
                    color: colors.primary.dark,
                    fontSize: "1.45rem",
                  }}
                >
                  {videoCount} Tutorials
                </Typography>
                <Typography variant="caption" sx={{ color: colors.primary.main, fontWeight: 600 }}>
                  Step-by-step walkthroughs
                </Typography>
              </Box>
            </Grid>

            {/* Metric 2: FAQs */}
            <Grid item xs={6}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: borderRadiusPx.lg,
                  bgcolor: colors.emerald[50],
                  border: `1px solid ${colors.emerald[200]}`,
                  height: "100%",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: borderRadiusPx.md,
                      bgcolor: colors.emerald[100],
                      color: colors.emerald[800],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FileQuestion size={15} />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.emerald[800],
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.7rem",
                    }}
                  >
                    Knowledge Base
                  </Typography>
                </Stack>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 900,
                    color: colors.emerald[900],
                    fontSize: "1.45rem",
                  }}
                >
                  {faqCount} Topics
                </Typography>
                <Typography variant="caption" sx={{ color: colors.emerald[700], fontWeight: 600 }}>
                  Comprehensive answers
                </Typography>
              </Box>
            </Grid>

            {/* Metric 3: Provably Fair */}
            <Grid item xs={6}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: borderRadiusPx.lg,
                  bgcolor: colors.amber[50],
                  border: `1px solid ${colors.amber[200]}`,
                  height: "100%",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: borderRadiusPx.md,
                      bgcolor: colors.amber[100],
                      color: colors.amber[800],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <ShieldCheck size={15} />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.amber[800],
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.7rem",
                    }}
                  >
                    Fairness
                  </Typography>
                </Stack>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 900,
                    color: colors.amber[900],
                    fontSize: "1.3rem",
                  }}
                >
                  Chainlink VRF
                </Typography>
                <Typography variant="caption" sx={{ color: colors.amber[700], fontWeight: 600 }}>
                  Cryptographic on-chain RNG
                </Typography>
              </Box>
            </Grid>

            {/* Metric 4: Dedicated Support */}
            <Grid item xs={6}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: borderRadiusPx.lg,
                  bgcolor: colors.purple[50],
                  border: `1px solid ${colors.purple[200]}`,
                  height: "100%",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: borderRadiusPx.md,
                      bgcolor: colors.purple[100],
                      color: colors.purple[800],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Mail size={15} />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.purple[800],
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.7rem",
                    }}
                  >
                    Assistance
                  </Typography>
                </Stack>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 900,
                    color: colors.purple[900],
                    fontSize: "1.3rem",
                  }}
                >
                  Email & Discord
                </Typography>
                <Typography variant="caption" sx={{ color: colors.purple[700], fontWeight: 600 }}>
                  info@autobetlottery.com
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Card>
  );
};

HelpHero.propTypes = {
  searchQuery: PropTypes.string,
  onSearchChange: PropTypes.func.isRequired,
  videoCount: PropTypes.number,
  faqCount: PropTypes.number,
  onScrollToSection: PropTypes.func.isRequired,
};

export default HelpHero;
